import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { authenticatedFetch } from '../../utils/api';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { useToast } from '../../hooks/use-toast';
import { 
  UserPlus, 
  Users, 
  Activity,
  Clock,
  Shield,
  Lock,
  Unlock,
  Edit,
  Trash2,
  RefreshCw,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';

const SubAdminManagement = () => {
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubAdmin, setSelectedSubAdmin] = useState(null);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const availablePermissions = [
    { id: 'manage_terms', label: 'Manage Terms', description: 'Create and manage academic terms' },
    { id: 'manage_subscriptions', label: 'Manage Subscriptions', description: 'View and manage teacher subscriptions' },
    { id: 'manage_users', label: 'Manage Users', description: 'View, edit, and delete user accounts' },
    { id: 'generate_lessons', label: 'Generate Lessons', description: 'Use AI to generate lesson notes' },
    { id: 'manage_assets', label: 'Manage Assets', description: 'Upload commissioner stamps and seals' },
    { id: 'view_stats', label: 'View Statistics', description: 'Access lesson notes statistics and reports' },
    { id: 'manage_seo', label: 'Manage SEO', description: 'Update SEO settings and meta tags' },
    { id: 'private_sector_subscription', label: 'Private Sector Subscription', description: 'Manage private sector teachers lesson notes subscriptions and view revenue' },
  ];

  useEffect(() => {
    fetchSubAdmins();
  }, []);

  const fetchSubAdmins = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`${API_BASE}/api/admin/sub-admins-detailed`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubAdmins(data);
      } else {
        throw new Error('Failed to fetch sub-admins');
      }
    } catch (error) {
      console.error('Error fetching sub-admins:', error);
      toast({
        title: "Error",
        description: "Failed to load sub-administrators",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleSubAdminStatus = async (subAdminId, currentStatus) => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/admin/sub-admins/${subAdminId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ is_blocked: !currentStatus })
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: `Sub-admin ${currentStatus ? 'unblocked' : 'blocked'} successfully`,
          variant: "default"
        });
        fetchSubAdmins();
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating sub-admin status:', error);
      toast({
        title: "Error",
        description: "Failed to update sub-admin status",
        variant: "destructive"
      });
    }
  };

  const resetSubAdminPassword = async (subAdminId) => {
    try {
      if (!newPassword || newPassword.length < 8) {
        toast({
          title: "Error",
          description: "Password must be at least 8 characters long",
          variant: "destructive"
        });
        return;
      }

      const response = await authenticatedFetch(`${API_BASE}/api/admin/sub-admins/${subAdminId}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ new_password: newPassword })
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Password reset successfully",
          variant: "default"
        });
        setShowPasswordDialog(false);
        setNewPassword('');
        setSelectedSubAdmin(null);
      } else {
        throw new Error('Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast({
        title: "Error",
        description: "Failed to reset password",
        variant: "destructive"
      });
    }
  };

  const deleteSubAdmin = async (subAdminId) => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/admin/sub-admins/${subAdminId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Sub-admin deleted successfully",
          variant: "default"
        });
        fetchSubAdmins();
      } else {
        throw new Error('Failed to delete sub-admin');
      }
    } catch (error) {
      console.error('Error deleting sub-admin:', error);
      toast({
        title: "Error",
        description: "Failed to delete sub-admin",
        variant: "destructive"
      });
    }
  };

  const viewSubAdminActivity = (subAdmin) => {
    setSelectedSubAdmin(subAdmin);
    setShowActivityDialog(true);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Sub-Administrator Management
          </CardTitle>
          <CardDescription>
            Monitor and manage sub-administrators, their activities, and access permissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {subAdmins.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sub-Admin</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead>Login Count</TableHead>
                    <TableHead>Activities</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subAdmins.map((admin) => (
                    <TableRow key={admin.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{admin.name}</p>
                          <p className="text-sm text-gray-600">{admin.email}</p>
                          <p className="text-xs text-gray-500">
                            Created: {new Date(admin.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {admin.is_active ? (
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                          {admin.is_blocked && (
                            <Badge variant="destructive">Blocked</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          {admin.last_login ? (
                            <div>
                              <p className="text-sm">{new Date(admin.last_login).toLocaleDateString()}</p>
                              <p className="text-xs text-gray-500">{new Date(admin.last_login).toLocaleTimeString()}</p>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">Never logged in</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{admin.login_count}</Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewSubAdminActivity(admin)}
                          className="flex items-center gap-1"
                        >
                          <Activity className="h-3 w-3" />
                          {admin.activities_count}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-32">
                          {admin.permissions.slice(0, 2).map((permission) => (
                            <Badge key={permission} variant="outline" className="text-xs">
                              {availablePermissions.find(p => p.id === permission)?.label.split(' ')[0] || permission}
                            </Badge>
                          ))}
                          {admin.permissions.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{admin.permissions.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedSubAdmin(admin);
                              setShowEditDialog(true);
                            }}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleSubAdminStatus(admin.id, admin.is_blocked)}
                            className={admin.is_blocked ? "text-green-600 hover:text-green-700" : "text-red-600 hover:text-red-700"}
                          >
                            {admin.is_blocked ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedSubAdmin(admin);
                              setShowPasswordDialog(true);
                            }}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Shield className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteSubAdmin(admin.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Sub-Administrators</h3>
              <p className="text-gray-600">Sub-administrators can be created in Settings → Sub-Admins</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Details Dialog */}
      <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Activity Log - {selectedSubAdmin?.name}
            </DialogTitle>
            <DialogDescription>
              Recent activities and login sessions for this sub-administrator
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Login Statistics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Total Logins:</span>
                    <Badge>{selectedSubAdmin?.login_count || 0}</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Last Login:</span>
                    <span className="text-gray-600">
                      {selectedSubAdmin?.last_login 
                        ? new Date(selectedSubAdmin.last_login).toLocaleString()
                        : 'Never'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Active Sessions:</span>
                    <Badge variant="outline">{selectedSubAdmin?.sessions_count || 0}</Badge>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Account Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Account Status:</span>
                    {selectedSubAdmin?.is_active ? (
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Access Status:</span>
                    {selectedSubAdmin?.is_blocked ? (
                      <Badge variant="destructive">Blocked</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800">Allowed</Badge>
                    )}
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Permissions:</span>
                    <Badge variant="outline">{selectedSubAdmin?.permissions?.length || 0}</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Alert>
              <Clock className="h-4 w-4" />
              <AlertDescription>
                Activity monitoring tracks login times, actions performed, and system access patterns for security and audit purposes.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowActivityDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Reset Password - {selectedSubAdmin?.name}
            </DialogTitle>
            <DialogDescription>
              Set a new password for this sub-administrator
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">Password must be at least 8 characters long</p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPasswordDialog(false);
                setNewPassword('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => resetSubAdminPassword(selectedSubAdmin?.id)}>
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubAdminManagement;